export interface FriendProfile { id: string; name: string; venmoUsername?: string | null; profilePictureUrl?: string | null; type?: string }
export interface FriendShare {
  userId: string; participantId: string; shareId?: string | null; total: number; paymentStatus: string;
  items?: { id?: string; description?: string; name?: string; amount: number; assignedTo?: string[] }[];
  itemsSubtotal?: number; taxShare?: number; tipShare?: number;
  surchargeShare?: number; surcharges?: Record<string, number>; discountShare?: number; discounts?: Record<string, number>;
}
export interface FriendSharedBill {
  id: string; revision?: number; title: string; date: string; currency: string; status?: string; total?: number;
  paidById?: string | null; recipientUserId?: string; shares: FriendShare[]; shareId?: string | null;
  thumbnailUrl?: string; emoji?: string; notes?: string; requirePaymentConfirmation?: boolean;
}
export interface FriendSharedData {
  userA: FriendProfile; userB: FriendProfile; bills: FriendSharedBill[]; previousBills?: FriendSharedBill[]; totalBillCount?: number;
  /** Present only when history was requested one page at a time (`?limit=`); older servers return everything and omit these. */
  hasMore?: boolean; nextCursor?: string | null; pageSize?: number;
}
export interface FriendEntry { bill: FriendSharedBill; share: FriendShare; payer: FriendProfile; debtor: FriendProfile; key: string }
/** The debtor's share of each bill: the non-payer share, between these two people only. One rule for open and previous rows. */
export function friendBillEntries(data: FriendSharedData, bills: FriendSharedBill[]): FriendEntry[] {
  return bills.flatMap(bill => (bill.shares || []).filter(share => share.userId !== bill.paidById && [data.userA.id, data.userB.id].includes(bill.paidById || '')).map(share => ({
    bill, share, payer: bill.paidById === data.userA.id ? data.userA : data.userB,
    debtor: share.userId === data.userA.id ? data.userA : data.userB, key: `${bill.id}:${share.participantId}`,
  })));
}
/** Open bills only: these feed the header totals and the payment picker. */
export function friendEntries(data: FriendSharedData): FriendEntry[] { return friendBillEntries(data, data.bills); }
export function payableFriendEntry(entry: FriendEntry): boolean {
  return !['paid','pending_confirmation'].includes(entry.share.paymentStatus) && Number.isFinite(Number(entry.share.total)) && Number(entry.share.total) > 0 && !['draft','editing','settled','processing'].includes(entry.bill.status || '');
}
/** One total per currency. Offsetting shares remain unpaid until explicitly recorded. */
export function friendTotals(entries: FriendEntry[], userAId: string) {
  const totals = new Map<string, { currency: string; owedToA: number; owedToB: number; netToA: number }>();
  for (const entry of entries) {
    const amount = Number(entry.share.total);
    if (!Number.isFinite(amount) || amount <= 0 || entry.share.paymentStatus === 'paid') continue;
    const currency = entry.bill.currency || 'USD';
    const total = totals.get(currency) || { currency, owedToA: 0, owedToB: 0, netToA: 0 };
    if (entry.bill.paidById === userAId) total.owedToA += amount; else total.owedToB += amount;
    total.netToA = Math.round((total.owedToA-total.owedToB)*100)/100;
    totals.set(currency,total);
  }
  return Array.from(totals.values());
}
export function shareBreakdown(share: FriendShare) {
  const rows: { label: string; amount: number }[] = (share.items || []).map(item => ({
    label: item.description || item.name || 'Item', amount: Number(item.amount) / (item.assignedTo?.length || 1),
  })).filter(row => Number.isFinite(row.amount));
  if (!rows.length && share.itemsSubtotal != null) rows.push({ label: 'Subtotal', amount: Number(share.itemsSubtotal) });
  if (share.taxShare) rows.push({ label: 'Tax', amount: Number(share.taxShare) });
  if (share.tipShare) rows.push({ label: 'Tip', amount: Number(share.tipShare) });
  for (const [label, amount] of Object.entries(share.surcharges || {})) if (amount) rows.push({ label, amount: Number(amount) });
  if (!Object.keys(share.surcharges || {}).length && share.surchargeShare) rows.push({ label: 'Fees', amount: Number(share.surchargeShare) });
  for (const [label, amount] of Object.entries(share.discounts || {})) if (amount) rows.push({ label, amount: -Math.abs(Number(amount)) });
  if (!Object.keys(share.discounts || {}).length && share.discountShare) rows.push({ label: 'Discount', amount: -Math.abs(Number(share.discountShare)) });
  return rows.filter(row => Number.isFinite(row.amount));
}
